import { useState, FormEvent } from "react";
import Toast from "@/components/base/Toast";

const FORM_URL = "https://readdy.ai/api/form/d7j30o2l1gjk5lvpjr30";

const EMPTY_FORM = {
  nombre: "",
  telefono: "",
  email: "",
  servicio: "",
  mensaje: "",
};

export default function Contact() {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "mensaje") {
      if (value.length > 500) return;
      setCharCount(value.length);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setToast(null);

    if (!formData.nombre.trim() || !formData.email.trim() || !formData.telefono.trim() || !formData.mensaje.trim()) {
      setToast({ type: "error", message: "Por favor rellena todos los campos obligatorios." });
      return;
    }

    setLoading(true);
    try {
      const body = new URLSearchParams();
      body.append("nombre", formData.nombre);
      body.append("telefono", formData.telefono);
      body.append("email", formData.email);
      body.append("servicio", formData.servicio);
      body.append("mensaje", formData.mensaje);

      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitted(true);
        setToast({ type: "success", message: "¡Mensaje enviado! Te responderemos en menos de 24 horas." });
      } else {
        throw new Error(`HTTP ${res.status}`);
      }
    } catch {
      setToast({ type: "error", message: "Error al enviar. Inténtalo de nuevo o llámanos directamente." });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    setFormData(EMPTY_FORM);
    setCharCount(0);
  };

  return (
    <section id="contacto" className="py-20 md:py-28 bg-slate-50">
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="text-cyan-500 text-sm font-semibold uppercase tracking-widest">Contacto</span>
          <h2 className="text-3xl md:text-5xl font-extrabold text-slate-800 mt-3 mb-4">
            Solicita tu{" "}
            <span className="text-cyan-500">Presupuesto Gratis</span>
          </h2>
          <p className="text-slate-500 max-w-xl mx-auto text-base">
            Cuéntanos tu proyecto y te responderemos en menos de 24 horas con un presupuesto personalizado sin compromiso.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 lg:gap-14">
          {/* Left: Info + Map */}
          <div className="w-full lg:w-[45%] flex flex-col gap-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: "ri-phone-fill", label: "Teléfono 1", value: "642 885 747", href: "tel:+34642885747", color: "text-cyan-500" },
                { icon: "ri-phone-fill", label: "Teléfono 2", value: "642 452 237", href: "tel:+34642452237", color: "text-teal-500" },
                { icon: "ri-whatsapp-fill", label: "WhatsApp", value: "642 885 747", href: "https://wa.me/34642885747", color: "text-green-500" },
                { icon: "ri-mail-fill", label: "Email", value: "andreidenis612@gmail.com", href: "mailto:andreidenis612@gmail.com", color: "text-sky-500" },
              ].map((c) => (
                <a
                  key={c.label}
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel={c.href.startsWith("http") ? "nofollow noreferrer" : undefined}
                  className="bg-white rounded-xl p-4 flex items-center gap-3 border border-slate-100 hover:border-cyan-200 transition-all cursor-pointer"
                >
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-slate-50 ${c.color} flex-shrink-0`}>
                    <i className={`${c.icon} text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">{c.label}</p>
                    <p className="text-slate-800 font-semibold text-sm">{c.value}</p>
                  </div>
                </a>
              ))}
            </div>

            {/* Address + Hours */}
            <div className="bg-white rounded-xl p-5 border border-slate-100">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-50 text-cyan-500 flex-shrink-0">
                  <i className="ri-map-pin-fill text-lg"></i>
                </div>
                <div>
                  <p className="text-slate-400 text-xs">Dirección</p>
                  <p className="text-slate-800 font-semibold text-sm">Calle Huesca, 20, Villarrubia de los Ojos, 13670</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-50 text-cyan-500 flex-shrink-0">
                  <i className="ri-time-fill text-lg"></i>
                </div>
                <div>
                  <p className="text-slate-400 text-xs">Horario de Atención</p>
                  <p className="text-slate-800 font-semibold text-sm">Lun–Vie: 8:00–19:00</p>
                  <p className="text-slate-600 text-xs">Sáb: 9:00–14:00 · Dom: Cerrado</p>
                </div>
              </div>
            </div>

            {/* Google Maps */}
            <div className="rounded-2xl overflow-hidden border border-slate-200 h-64 w-full">
              <iframe
                title="Ubicación SUPERPISCINAS FLORIN"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3101.2!2d-3.5983!3d39.0167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd6a1e3b3b3b3b3b%3A0x0!2sCalle+Huesca+20%2C+Villarrubia+de+los+Ojos%2C+13670!5e0!3m2!1ses!2ses!4v1700000000000!5m2!1ses!2ses&q=Calle+Huesca+20,+Villarrubia+de+los+Ojos,+13670,+Ciudad+Real,+España"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

          {/* Right: Form */}
          <div className="w-full lg:w-[55%]">
            <div className="bg-white rounded-2xl p-8 border border-slate-100">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mb-4">
                    <i className="ri-check-line text-green-500 text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">¡Mensaje enviado!</h3>
                  <p className="text-slate-500 text-sm max-w-xs">
                    Gracias por contactarnos. Te responderemos en menos de 24 horas con tu presupuesto personalizado.
                  </p>
                  <button
                    onClick={handleReset}
                    className="mt-6 bg-cyan-500 text-white font-semibold px-6 py-2.5 rounded-full text-sm cursor-pointer whitespace-nowrap"
                  >
                    Enviar otro mensaje
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="contacto-form"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <h3 className="text-xl font-bold text-slate-800 mb-6">Cuéntanos tu proyecto</h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-slate-600 text-sm font-medium mb-1.5">Nombre *</label>
                      <input
                        type="text"
                        name="nombre"
                        required
                        value={formData.nombre}
                        onChange={handleChange}
                        placeholder="Tu nombre completo"
                        maxLength={80}
                        autoComplete="name"
                        className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-cyan-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-600 text-sm font-medium mb-1.5">Teléfono *</label>
                      <input
                        type="tel"
                        name="telefono"
                        required
                        value={formData.telefono}
                        onChange={handleChange}
                        placeholder="+34 600 000 000"
                        maxLength={20}
                        autoComplete="tel"
                        className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-cyan-400 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-slate-600 text-sm font-medium mb-1.5">Email *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="tu@email.com"
                      maxLength={100}
                      autoComplete="email"
                      className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-cyan-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 text-sm font-medium mb-1.5">Servicio de interés</label>
                    <select
                      name="servicio"
                      value={formData.servicio}
                      onChange={handleChange}
                      className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-cyan-400 transition-colors bg-white cursor-pointer"
                    >
                      <option value="">Selecciona un servicio</option>
                      <option value="Construcción de piscina">Construcción de piscina</option>
                      <option value="Mantenimiento">Mantenimiento</option>
                      <option value="Limpieza">Limpieza</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-slate-600 text-sm font-medium mb-1.5">Mensaje *</label>
                    <textarea
                      name="mensaje"
                      required
                      value={formData.mensaje}
                      onChange={handleChange}
                      rows={4}
                      maxLength={500}
                      placeholder="Cuéntanos los detalles de tu proyecto, dimensiones, etc."
                      className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-cyan-400 transition-colors resize-none"
                    ></textarea>
                    <p className={`text-xs mt-1 text-right ${charCount >= 500 ? "text-red-500" : "text-slate-400"}`}>
                      {charCount}/500
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-cyan-500 hover:bg-cyan-600 disabled:opacity-60 text-white font-bold py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap text-base"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        Enviando...
                      </span>
                    ) : "Enviar Solicitud →"}
                  </button>

                  <div className="flex items-center justify-center gap-2 text-slate-400 text-xs">
                    <i className="ri-shield-check-line text-cyan-400"></i>
                    <span>Formulario seguro. Tus datos están protegidos.</span>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
